import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type SyntheticEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { Avatar } from '../Avatar/Avatar';
import { InputTextArea } from '../Input/InputTextArea';
import { TagGeneral } from '../Tag/TagGeneral';
import { useAnchoredPanelPosition } from '../../hooks/useAnchoredPanelPosition';
import { useControllableState } from '../../hooks/useControllableState';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useListNavigation } from '../../hooks/useListNavigation';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import { getOverlayPortalParent } from '../../utils/getOverlayPortalParent';
import { mergeRefs } from '../../utils/mergeRefs';
import {
  filterMentionUsers,
  getMentionQuery,
  removeMentionToken,
  type MentionQuery,
} from './mentionQuery';
import type { MentionTextAreaProps, MentionUser } from './MentionTextArea.types';
import './MentionTextArea.css';

const EMPTY_IDS: string[] = [];

export const MentionTextArea = forwardRef<HTMLTextAreaElement, MentionTextAreaProps>(
  function MentionTextArea(
    {
      users,
      selectedIds,
      defaultSelectedIds = EMPTY_IDS,
      onSelectedIdsChange,
      value,
      defaultValue = '',
      onChange,
      maxSelected,
      noResultsText = 'No matching members',
      panelClassName = '',
      className = '',
      onKeyDown,
      onSelect,
      onBlur,
      disabled,
      readOnly,
      ...textAreaProps
    },
    ref,
  ) {
    const listId = useId();
    const rootRef = useRef<HTMLDivElement>(null);
    const fieldRef = useRef<HTMLTextAreaElement>(null);
    const boxRef = useRef<HTMLElement | null>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const pendingCaretRef = useRef<number | null>(null);

    const [text, setText] = useControllableState<string>(value, defaultValue, onChange);
    const [selected, setSelected] = useControllableState<string[]>(
      selectedIds,
      defaultSelectedIds,
      onSelectedIdsChange,
    );
    const [mention, setMention] = useState<MentionQuery | null>(null);

    const atLimit = maxSelected !== undefined && selected.length >= maxSelected;
    const isOpen = mention !== null && !atLimit && !disabled && !readOnly;

    const candidates = useMemo(
      () => (mention ? filterMentionUsers(users, mention.query, selected) : []),
      [users, mention, selected],
    );

    const { activeIndex, setActiveIndex, handleKeyDown: handleListKeyDown } = useListNavigation({
      itemCount: candidates.length,
    });

    // The picker hangs off the bordered box (not the whole field) so it does not
    // overlap the helper text; the box is the closest InputTextArea wrapper.
    useLayoutEffect(() => {
      boxRef.current =
        (fieldRef.current?.closest('.spark-input-textarea-box') as HTMLElement | null) ?? null;
    });

    const panelStyle = useAnchoredPanelPosition(boxRef, panelRef, isOpen);

    const close = useCallback(() => setMention(null), []);
    useOutsideClick([rootRef, panelRef], close, isOpen);
    useEscapeKey(close, isOpen);

    // Highlight the first match whenever the list changes so Enter always picks something.
    useEffect(() => {
      if (!isOpen) return;
      setActiveIndex(candidates.length > 0 ? 0 : -1);
    }, [isOpen, candidates, setActiveIndex]);

    // Keep the highlighted row visible in a long list.
    useEffect(() => {
      if (!isOpen || activeIndex < 0) return;
      const row = panelRef.current?.querySelector<HTMLElement>(
        `[data-mention-index="${activeIndex}"]`,
      );
      row?.scrollIntoView({ block: 'nearest' });
    }, [isOpen, activeIndex]);

    // Restore the caret after a selection rewrote the value.
    useLayoutEffect(() => {
      const caret = pendingCaretRef.current;
      if (caret === null) return;
      pendingCaretRef.current = null;
      const field = fieldRef.current;
      if (!field) return;
      field.focus();
      field.setSelectionRange(caret, caret);
    }, [text]);

    const syncMention = useCallback((nextValue: string, caret: number) => {
      setMention(getMentionQuery(nextValue, caret));
    }, []);

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
      const next = event.target.value;
      setText(next);
      syncMention(next, event.target.selectionStart ?? next.length);
    };

    const handleSelectEvent = (event: SyntheticEvent<HTMLTextAreaElement>) => {
      const field = event.currentTarget;
      syncMention(field.value, field.selectionStart ?? field.value.length);
      onSelect?.(event);
    };

    const pick = useCallback(
      (user: MentionUser) => {
        if (!mention) return;
        const rewritten = removeMentionToken(text, mention);
        pendingCaretRef.current = rewritten.caret;
        setText(rewritten.value);
        setSelected((prev) => (prev.includes(user.id) ? prev : [...prev, user.id]));
        setMention(null);
      },
      [mention, text, setText, setSelected],
    );

    const remove = useCallback(
      (id: string) => {
        setSelected((prev) => prev.filter((item) => item !== id));
        fieldRef.current?.focus();
      },
      [setSelected],
    );

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (isOpen) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Home' || event.key === 'End') {
          if (candidates.length > 0) {
            handleListKeyDown(event);
            return;
          }
        }
        if ((event.key === 'Enter' && !event.shiftKey) || event.key === 'Tab') {
          const target = candidates[activeIndex] ?? candidates[0];
          if (target) {
            event.preventDefault();
            pick(target);
            return;
          }
        }
      }
      onKeyDown?.(event);
    };

    const selectedUsers = selected
      .map((id) => users.find((user) => user.id === id))
      .filter((user): user is MentionUser => Boolean(user));

    const chips =
      selectedUsers.length > 0 ? (
        <div className="spark-mention-textarea-chips" aria-label="Selected members">
          {selectedUsers.map((user) => (
            <TagGeneral
              key={user.id}
              label={user.name}
              variant={disabled || readOnly ? 'default' : 'closeable'}
              onClose={disabled || readOnly ? undefined : () => remove(user.id)}
            />
          ))}
        </div>
      ) : undefined;

    const activeId = activeIndex >= 0 && candidates[activeIndex] ? `${listId}-${candidates[activeIndex].id}` : undefined;

    const panel = isOpen ? (
      <div
        ref={panelRef}
        id={listId}
        role="listbox"
        aria-label="Mention a member"
        className={['spark-mention-textarea-panel', panelClassName].filter(Boolean).join(' ')}
        style={panelStyle}
      >
        {candidates.length === 0 ? (
          <div className="spark-mention-textarea-empty">{noResultsText}</div>
        ) : (
          <div className="spark-mention-textarea-list">
            {candidates.map((user, index) => (
              <div
                key={user.id}
                id={`${listId}-${user.id}`}
                role="option"
                aria-selected={index === activeIndex}
                data-mention-index={index}
                className={[
                  'spark-mention-textarea-option',
                  index === activeIndex ? 'spark-mention-textarea-option-active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => pick(user)}
              >
                <Avatar
                  size="small"
                  type={user.avatarUrl ? 'image' : 'initials'}
                  src={user.avatarUrl}
                  primaryText={user.name}
                  alt=""
                />
                <span className="spark-mention-textarea-option-name">{user.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    ) : null;

    return (
      <div
        ref={rootRef}
        className={['spark-mention-textarea', className].filter(Boolean).join(' ')}
      >
        <InputTextArea
          {...textAreaProps}
          ref={mergeRefs(ref, fieldRef)}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onSelect={handleSelectEvent}
          onBlur={onBlur}
          disabled={disabled}
          readOnly={readOnly}
          attachments={chips}
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listId : undefined}
          aria-activedescendant={isOpen ? activeId : undefined}
        />
        {panel && rootRef.current
          ? createPortal(panel, getOverlayPortalParent(rootRef.current))
          : null}
      </div>
    );
  },
);

MentionTextArea.displayName = 'MentionTextArea';
